import { useEffect, useState } from 'react'
import { IAPIPrincipal } from 'extra/types/ApiModels'
import { Permission, ALL_PERMISSIONS } from 'extra/types/Permissions'
import { Trash2, User, Bot, Folder, Plus, Edit2 } from 'lucide-react'
import { api } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import { useAuth } from '../context/AuthContext'

const PERMISSION_LABELS: Record<Permission, string> = {
  [Permission.MANAGE_PRINCIPALS]: 'Manage Users',
  [Permission.MANAGE_DEPLOYMENTS]: 'Manage Deployments',
  [Permission.VIEW_EXECUTIONS]: 'View Executions',
}

export function SettingsPage () {
  const [principals, setPrincipals] = useState<Array<IAPIPrincipal>>([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [updatingPermissionsId, setUpdatingPermissionsId] = useState<string | null>(null)

  const { user: currentUser, hasPermission } = useAuth()
  const canManagePrincipals = hasPermission(Permission.MANAGE_PRINCIPALS)

  // Folder management state
  const { folders, prompts, refreshFolders } = useEntities()
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingFolderName, setEditingFolderName] = useState('')
  const [folderError, setFolderError] = useState<string | null>(null)
  const [creatingFolder, setCreatingFolder] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const principalsData = await api.principals.list()
      setPrincipals(principalsData)
    } catch (error) {
      console.error('Failed to load settings data', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this principal?')) return

    setDeletingId(id)
    try {
      await api.principals.delete(id)
      setPrincipals(principals.filter(p => p.id !== id))
    } catch (error) {
      console.error('Failed to delete principal', error)
      alert('Failed to delete principal')
    } finally {
      setDeletingId(null)
    }
  }

  const handleTogglePermission = async (principalId: string, permission: Permission, currentPermissions: Array<string> | null) => {
    const perms = currentPermissions ?? []
    const newPermissions = perms.includes(permission)
      ? perms.filter(p => p !== permission)
      : [...perms, permission]

    setUpdatingPermissionsId(principalId)
    try {
      const updated = await api.principals.updatePermissions(principalId, newPermissions)
      setPrincipals(prev => prev.map(p => p.id === updated.id ? updated : p))
    } catch (error) {
      console.error('Failed to update permissions', error)
      alert('Failed to update permissions')
    } finally {
      setUpdatingPermissionsId(null)
    }
  }

  // Folder management functions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    setCreatingFolder(true)
    setFolderError(null)
    try {
      await api.folders.create({ name: newFolderName.trim() })
      await refreshFolders()
      setNewFolderName('')
      setShowCreateFolder(false)
    } catch (error: any) {
      if (error.response?.status === 409) {
        setFolderError('A folder with this name already exists')
      } else {
        setFolderError('Failed to create folder')
      }
    } finally {
      setCreatingFolder(false)
    }
  }

  const handleUpdateFolder = async (id: string, name: string) => {
    if (!name.trim()) return

    setFolderError(null)
    try {
      await api.folders.update(id, { name: name.trim() })
      await refreshFolders()
      setEditingFolderId(null)
      setEditingFolderName('')
    } catch (error: any) {
      if (error.response?.status === 409) {
        setFolderError('A folder with this name already exists')
      } else {
        setFolderError('Failed to update folder')
      }
    }
  }

  const handleDeleteFolder = async (id: string, name: string) => {
    const promptsInFolder = prompts.filter(p => p.folderId === id)
    const message = promptsInFolder.length > 0
      ? `Are you sure you want to delete "${name}"? ${promptsInFolder.length} prompt(s) will become uncategorized.`
      : `Are you sure you want to delete "${name}"?`

    if (!confirm(message)) return

    try {
      await api.folders.delete(id)
      await refreshFolders()
    } catch (error) {
      console.error('Failed to delete folder', error)
      alert('Failed to delete folder')
    }
  }

  const users = principals.filter(p => !p.isServiceAccount)
  const serviceAccounts = principals.filter(p => p.isServiceAccount)

  if (loading) return <div className="text-gray-400">Loading...</div>

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-8">Settings</h1>

      <div className="space-y-8">
        {/* Folders Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Folder className="h-5 w-5 text-green-400" />
              <h2 className="text-xl font-semibold text-gray-200">Folders</h2>
            </div>
            <button
              onClick={() => setShowCreateFolder(!showCreateFolder)}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
            >
              <Plus className="h-4 w-4" />
              New Folder
            </button>
          </div>

          {showCreateFolder && (
            <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  placeholder="Folder name"
                  className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
                <button
                  onClick={handleCreateFolder}
                  disabled={creatingFolder || !newFolderName.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowCreateFolder(false)
                    setNewFolderName('')
                    setFolderError(null)
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
              {folderError && (
                <div className="mt-2 text-sm text-red-400">{folderError}</div>
              )}
            </div>
          )}

          <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Prompts</th>
                  <th className="px-6 py-3">Created At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {folders.map(folder => {
                  const promptCount = prompts.filter(p => p.folderId === folder.id).length
                  const isEditing = editingFolderId === folder.id

                  return (
                    <tr key={folder.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 text-gray-200 font-medium">
                        {isEditing
                          ? (
                          <input
                            type="text"
                            value={editingFolderName}
                            onChange={(e) => setEditingFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateFolder(folder.id, editingFolderName)
                              if (e.key === 'Escape') {
                                setEditingFolderId(null)
                                setEditingFolderName('')
                                setFolderError(null)
                              }
                            }}
                            className="px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                            autoFocus
                          />
                            )
                          : (
                              folder.name
                            )}
                      </td>
                      <td className="px-6 py-4 text-gray-400">
                        {promptCount} {promptCount === 1 ? 'prompt' : 'prompts'}
                      </td>
                      <td className="px-6 py-4 text-gray-500 font-mono text-xs">
                        {`${new Date(folder.createdAt || '').toLocaleDateString('en-US', { timeZone: 'UTC' })} UTC`}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {isEditing
                          ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleUpdateFolder(folder.id, editingFolderName)}
                              disabled={!editingFolderName.trim()}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors text-xs"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingFolderId(null)
                                setEditingFolderName('')
                                setFolderError(null)
                              }}
                              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                            )
                          : (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditingFolderId(folder.id)
                                setEditingFolderName(folder.name)
                                setFolderError(null)
                              }}
                              className="p-2 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300 rounded-lg transition-colors"
                              title="Rename folder"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteFolder(folder.id, folder.name)}
                              className="p-2 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-lg transition-colors"
                              title="Delete folder"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                            )}
                      </td>
                    </tr>
                  )
                })}
                {folders.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      No folders yet. Create one to organize your prompts.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {folderError && !showCreateFolder && !editingFolderId && (
            <div className="mt-2 text-sm text-red-400">{folderError}</div>
          )}
        </section>

        {/* Users Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-200">Users</h2>
          </div>

          <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3">Permissions</th>
                  <th className="px-6 py-3">Created At</th>
                  {canManagePrincipals && <th className="px-6 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {users.map(user => {
                  const isSelf = user.id === currentUser?.id
                  return (
                    <tr key={user.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 text-gray-200 font-medium">
                        {user.name}
                        {isSelf && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-300 rounded-full">
                            You
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-400">{user.email}</td>
                      <td className="px-6 py-4">
                        {canManagePrincipals && !isSelf
                          ? (
                          <div className="flex flex-wrap gap-2">
                            {ALL_PERMISSIONS.map(perm => {
                              const checked = (user.permissions ?? []).includes(perm)
                              return (
                                <label key={perm} className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={updatingPermissionsId === user.id}
                                    onChange={() => handleTogglePermission(user.id, perm, user.permissions)}
                                    className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                                  />
                                  <span className="text-xs text-gray-300">{PERMISSION_LABELS[perm]}</span>
                                </label>
                              )
                            })}
                          </div>
                            )
                          : (
                          <div className="flex flex-wrap gap-1.5">
                            {(user.permissions ?? []).map(perm => (
                              <span key={perm} className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded-full">
                                {PERMISSION_LABELS[perm as Permission] ?? perm}
                              </span>
                            ))}
                            {(!user.permissions || user.permissions.length === 0) && (
                              <span className="text-xs text-gray-500">None</span>
                            )}
                          </div>
                            )}
                      </td>
                      <td className="px-6 py-4 text-gray-500 font-mono text-xs">
                        {`${new Date(user.createdAt || '').toLocaleDateString('en-US', { timeZone: 'UTC' })} UTC`}
                      </td>
                      {canManagePrincipals && (
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={isSelf || deletingId === user.id}
                            className={`
                              p-2 rounded-lg transition-colors
                              ${isSelf
                                ? 'text-gray-600 cursor-not-allowed'
                                : 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                              }
                            `}
                            title={isSelf ? 'Cannot delete yourself' : 'Delete user'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={canManagePrincipals ? 5 : 4} className="px-6 py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Service Accounts Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Bot className="h-5 w-5 text-purple-400" />
            <h2 className="text-xl font-semibold text-gray-200">Service Accounts</h2>
          </div>

          <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Created At</th>
                  {canManagePrincipals && <th className="px-6 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {serviceAccounts.map(sa => (
                  <tr key={sa.id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 text-gray-200 font-medium">{sa.name}</td>
                    <td className="px-6 py-4 text-gray-500 font-mono text-xs">
                      {`${new Date(sa.createdAt || '').toLocaleDateString('en-US', { timeZone: 'UTC' })} UTC`}
                    </td>
                    {canManagePrincipals && (
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(sa.id)}
                          disabled={deletingId === sa.id}
                          className="p-2 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-lg transition-colors"
                          title="Delete service account"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {serviceAccounts.length === 0 && (
                  <tr>
                    <td colSpan={canManagePrincipals ? 3 : 2} className="px-6 py-8 text-center text-gray-500">
                      No service accounts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  )
}
